import { Injectable, inject } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from '@env/environment';
import { UserEnvService } from '@core/services/user-env/user-env.service';
import {
  REPORT_PAYLOAD_REQUEST,
  REPORT_PAYLOAD_RESPONSE,
  ReportDesignerPayload,
  ReportPayloadBase,
  ReportPayloadChannel,
  ReportPayloadRequestMessage,
  ReportPayloadResponseMessage,
  ReportViewerPayload,
} from './report-payload.protocol';

@Injectable()
export abstract class ReportLaunchServiceBase {
  private static listenerRegistered = false;
  private static readonly pendingPayloads = new Map<
    string,
    ReportViewerPayload | ReportDesignerPayload
  >();
  private static readonly pendingTimers = new Map<string, number>();

  private readonly userEnvService = inject(UserEnvService);
  private readonly keycloak = inject(Keycloak);

  protected constructor() {
    ReportLaunchServiceBase.ensureMessageListener(
      environment.reportingAppUrl.replace(/\/$/, ''),
    );
  }

  protected openReportWindow(
    channel: ReportPayloadChannel,
    route: 'report-viewer' | 'report-designer',
    payload: ReportViewerPayload | ReportDesignerPayload,
  ): boolean {
    this.clearStaleEntries(channel);

    const token = this.generateId();
    const enriched = this.enrichPayload(payload);
    ReportLaunchServiceBase.pendingPayloads.set(`${channel}:${token}`, enriched);
    ReportLaunchServiceBase.scheduleCleanup(`${channel}:${token}`);

    const base = environment.reportingAppUrl.replace(/\/$/, '');
    const url = `${base}/${route}?token=${encodeURIComponent(token)}`;
    const opened = window.open(url, '_blank');
    return opened != null;
  }

  private enrichPayload<T extends ReportPayloadBase>(payload: T): T {
    const userEnv = this.userEnvService.env();
    const workingPeriodId =
      payload.working_period_id ?? this.userEnvService.env()?.working_period?.id ?? null;
    const apiBaseUrl = (payload.apiBaseUrl ?? environment.apiUrl).replace(/\/$/, '');
    const authHeaders: Record<string, string> = {
      ...(payload.authHeaders ?? {}),
    };

    if (workingPeriodId != null) {
      authHeaders['working-period-id'] = String(workingPeriodId);
    }

    const accessToken = this.keycloak.token;
    if (accessToken && !authHeaders['Authorization']) {
      authHeaders['Authorization'] = `Bearer ${accessToken}`;
    }

    const deviceId = localStorage.getItem('x-device-id');
    if (deviceId && !authHeaders['x-device-id']) {
      authHeaders['x-device-id'] = deviceId;
    }

    return {
      ...payload,
      apiBaseUrl,
      working_period_id: workingPeriodId ?? undefined,
      language:
        payload.language ??
        userEnv?.language ??
        localStorage.getItem('current_language') ??
        'tr',
      dateFormat: payload.dateFormat ?? userEnv?.date_format ?? 'DD.MM.YYYY',
      authHeaders,
    };
  }

  private clearStaleEntries(channel: ReportPayloadChannel): void {
    for (const key of ReportLaunchServiceBase.pendingPayloads.keys()) {
      if (key.startsWith(`${channel}:`)) {
        ReportLaunchServiceBase.pendingPayloads.delete(key);
        const timer = ReportLaunchServiceBase.pendingTimers.get(key);
        if (timer) {
          window.clearTimeout(timer);
          ReportLaunchServiceBase.pendingTimers.delete(key);
        }
      }
    }
  }

  private static scheduleCleanup(key: string): void {
    const timer = window.setTimeout(() => {
      ReportLaunchServiceBase.pendingPayloads.delete(key);
      ReportLaunchServiceBase.pendingTimers.delete(key);
    }, environment.reportPayloadTtlMs ?? 60000);
    ReportLaunchServiceBase.pendingTimers.set(key, timer);
  }

  private static ensureMessageListener(reportingAppUrl: string): void {
    if (ReportLaunchServiceBase.listenerRegistered) {
      return;
    }

    window.addEventListener('message', (event: MessageEvent) => {
      const allowedOrigin = new URL(reportingAppUrl).origin;
      if (event.origin !== allowedOrigin) {
        return;
      }

      const data = event.data as Partial<ReportPayloadRequestMessage>;
      if (data?.type !== REPORT_PAYLOAD_REQUEST || !data.token || !data.channel) {
        return;
      }

      const key = `${data.channel}:${data.token}`;
      const payload = ReportLaunchServiceBase.pendingPayloads.get(key);
      if (!payload) {
        return;
      }

      const response: ReportPayloadResponseMessage = {
        type: REPORT_PAYLOAD_RESPONSE,
        token: data.token,
        payload: payload as ReportPayloadResponseMessage['payload'],
      };
      event.source?.postMessage(response, { targetOrigin: event.origin });
      ReportLaunchServiceBase.pendingPayloads.delete(key);

      const timer = ReportLaunchServiceBase.pendingTimers.get(key);
      if (timer) {
        window.clearTimeout(timer);
        ReportLaunchServiceBase.pendingTimers.delete(key);
      }
    });

    ReportLaunchServiceBase.listenerRegistered = true;
  }

  private generateId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }
}
